import React, { useState } from 'react';
import { X, Copy, ExternalLink, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

/**
 * PromptModal Component
 * 
 * Displays a detailed modal view of a prompt with copy functionality.
 * Supports both single prompts and multi-part prompt modules.
 * 
 * Single Prompt Display:
 * - Shows title, description, prompt text, and usage instructions
 * - "Copy Full Prompt" button copies the entire prompt text
 * 
 * Multi-part Prompt Display:
 * - Shows module title and description at the top
 * - Displays each part in order with individual sections
 * - Each part shows: title, description, prompt text, usage instructions
 * - Individual "Copy Part" buttons for each part
 * - "Copy All Parts" button combines all parts with separators
 * 
 * @param {Object} prompt - The prompt object to display
 * @param {boolean} prompt.is_multi_part - Whether this is a multi-part prompt
 * @param {Array} prompt.parts - Array of parts for multi-part prompts
 * @param {Function} onClose - Function to call when modal is closed
 * @param {Function} onCopy - Function to call when copying prompt text
 * @returns {JSX.Element} Modal component for prompt display
 */
const PromptModal = ({ prompt, onClose, onCopy }) => {
  const [copySuccess, setCopySuccess] = useState('');

  const handleCopyPrompt = async () => {
    try {
      await onCopy(prompt.prompt_text);
      setCopySuccess('Prompt copied to clipboard!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (error) {
      console.error('Failed to copy prompt:', error);
    }
  };

  const handleCopyPart = async (partText) => {
    try {
      await navigator.clipboard.writeText(partText);
      setCopySuccess('Part copied to clipboard!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (error) {
      console.error('Failed to copy part:', error);
    }
  };

  const handleCopyAllParts = async () => {
    try {
      if (prompt.parts && prompt.parts.length > 0) {
        const allPartsText = prompt.parts
          .map((part, index) => `Part ${part.part_number}: ${part.title}\n\n${part.prompt_text}`)
          .join('\n\n---\n\n');
        await navigator.clipboard.writeText(allPartsText);
        setCopySuccess('All parts copied to clipboard!');
        setTimeout(() => setCopySuccess(''), 3000);
      }
    } catch (error) {
      console.error('Failed to copy all parts:', error);
    }
  };

  const handleCopyLink = async () => {
    try {
      const url = `${window.location.origin}/prompts?prompt=${prompt.id}`;
      await navigator.clipboard.writeText(url);
      setCopySuccess('Link copied to clipboard!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'Marketing & Sales': '📊',
      'Financial Analysis': '💰',
      'Team Management': '👥',
      'Operations': '⚙️',
      'Product Development': '🚀',
      'Customer Research': '🔍'
    };
    return iconMap[categoryName] || '📝';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <span className="text-3xl">{getCategoryIcon(prompt.category_name)}</span>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{prompt.title}</h2>
              <div className="flex items-center space-x-2 mt-1">
                <Badge 
                  variant="secondary" 
                  style={{ backgroundColor: `${prompt.category_color}20`, color: prompt.category_color }}
                >
                  📂 {prompt.category_name}
                </Badge>
                <span className="text-sm text-gray-500">
                  Added {new Date(prompt.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="rounded-full hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Description */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
            <p className="text-gray-700 leading-relaxed">{prompt.description}</p>
          </div>

          {/* Prompt Text */}
          {prompt.is_multi_part ? (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Prompt Parts</h3>
              <div className="space-y-6">
                {prompt.parts && prompt.parts.map((part, index) => (
                  <div key={part.id || index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-semibold text-gray-900">
                        Part {part.part_number}: {part.title}
                      </h4>
                      <Button
                        size="sm"
                        onClick={() => handleCopyPart(part.prompt_text)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy Part
                      </Button>
                    </div>
                    {part.description && (
                      <p className="text-sm text-gray-600 mb-3">{part.description}</p>
                    )}
                    <div className="relative">
                      <pre className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto border">
                        {part.prompt_text}
                      </pre>
                    </div>
                    {part.usage_instructions && (
                      <div className="mt-3 bg-blue-50 p-3 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-900 mb-1">Usage Instructions:</h5>
                        <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                          {part.usage_instructions}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Prompt Text</h3>
              <div className="relative">
                <pre className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto border">
                  {prompt.prompt_text}
                </pre>
                <Button
                  size="sm"
                  onClick={handleCopyPrompt}
                  className="absolute top-2 right-2 bg-blue-600 hover:bg-blue-700"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </Button>
              </div>
            </div>
          )}

          {/* Usage Instructions */}
          {prompt.usage_instructions && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Usage Instructions</h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                  {prompt.usage_instructions}
                </pre>
              </div>
            </div>
          )}

          {/* Success Message */}
          {copySuccess && (
            <div className="mb-4 p-3 bg-green-100 border border-green-200 rounded-lg flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800 text-sm">{copySuccess}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-3">
            <Button
              onClick={prompt.is_multi_part ? handleCopyAllParts : handleCopyPrompt}
              className="bg-blue-600 hover:bg-blue-700 flex items-center space-x-2"
            >
              <Copy className="w-4 h-4" />
              <span>{prompt.is_multi_part ? 'Copy All Parts' : 'Copy Full Prompt'}</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleCopyLink}
              className="flex items-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Copy Link</span>
            </Button>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PromptModal;
